<template>
  <div 
    :id="`${source}_chart_section`" 
    class="w-full h-full"
  >
    <AnalyticsChart
      v-if="
        dashboardType === analyticsEditorTypes.MANUAL ||
        dashboardType === analyticsEditorTypes.SQL
      "
      :source="source"
      :chart-type="chartType"
      :chart-data="chartData"
      :chart-config="props.chartConfig"
      :height="height"
      :show-data-table="props.showDataTable"
      @page-change="handlePageChange"
    />

    <iframe
      v-else-if="iframeSrc"
      ref="iframeRef"
      :src="iframeSrc"
      class="w-full border-none"
      :style="{ height: iframeHeight }"
      sandbox="allow-scripts allow-same-origin"
      @load="handleIframeLoad"
    />
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted, computed, nextTick } from "vue";
import { useStore } from "vuex";
import { analyticsEditorTypes } from "@/src/constants/analyticConstants.js";
import AnalyticsChart from "@/src/components/analytics_components/v3/chart/AnalyticsChart.vue";

const props = defineProps({
  source: { type: String, default: "" },
  content: { type: String, default: "" },
  dashboardType: { type: String, default: analyticsEditorTypes.AI },
  height: { type: [String, Number], default: "100%" },
  chartType: { type: String, default: null },
  chartData: { type: Object, default: null },
  chartConfig: { type: Object, default: null },
  showDataTable: { type: Boolean, default: false },
});

const store = useStore();

const iframeSrc = ref("");
const iframeRef = ref(null);
const iframeHeight = ref("0px");
let blobUrl = null;

const chartType = computed(() => {
  return props.chartType || store.getters["analyticsConfiguration/renderedChartType"];
});

const chartData = computed(() => {
  return props.chartData || store.getters["analyticsConfiguration/chartData"];
});

const handleIframeLoad = () => {
  nextTick(() => {
    try {
      if (!iframeRef.value) return;

      const iframe = iframeRef.value;
      const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;

      if (iframeDocument) {
        const contentHeight = iframeDocument.body?.scrollHeight || 
                             iframeDocument.documentElement?.scrollHeight || 
                             600;
        
        iframeHeight.value = `${contentHeight}px`;
      }
      else{
        iframeHeight.value = "600px";
      }
    } catch (error) {
      console.error("Error calculating iframe height:", error);
      iframeHeight.value = "600px";
    }
  });
};

const createBlobUrl = (htmlContent) => {
  try {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
    }

    const blob = new Blob([htmlContent], { type: "text/html" });
    blobUrl = URL.createObjectURL(blob);

    return blobUrl;
  } catch (error) {
    console.error("An error occurred while creating blob url: ", error);
  }
};

watch(
  () => props.content,
  (newContent) => {
    if (newContent && props.dashboardType === analyticsEditorTypes.AI) {
      iframeSrc.value = createBlobUrl(newContent);
    }
  },
  { immediate: true },
);

onUnmounted(() => {
  if (blobUrl) {
    URL.revokeObjectURL(blobUrl);
  }
});

const emit = defineEmits(["pageChange"]);

const handlePageChange = (paginationInfo) => {
  emit("pageChange", paginationInfo);
};

</script>
